import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { Title, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { MADRASA_SETTINGS_EDIT, MADRASA_SETTINGS_VIEW } from "../shared/madrasa-permissions";
import {
  DEFAULT_RECEIPT_PREFIX_TEMPLATE,
  previewReceiptPrefix,
  validateReceiptPrefixTemplate,
} from "./receipt-prefix";

type SettingsPayload = { receiptPrefixTemplate?: string };

export default function MadrasaSettingsPage() {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, MADRASA_SETTINGS_VIEW);
  const canEdit = hasPermission(permissions, MADRASA_SETTINGS_EDIT);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(DEFAULT_RECEIPT_PREFIX_TEMPLATE);
  const [template, setTemplate] = useState(DEFAULT_RECEIPT_PREFIX_TEMPLATE);

  const load = useCallback(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    callApi({ location: "madrasaSettings", method: "GET" })
      .then((response) => {
        const value =
          (response.data as SettingsPayload).receiptPrefixTemplate ??
          DEFAULT_RECEIPT_PREFIX_TEMPLATE;
        setSaved(value);
        setTemplate(value);
      })
      .catch((error: unknown) =>
        notify(apiErrorMessage(error, "Failed to load madrasa settings"), { type: "error" })
      )
      .finally(() => setLoading(false));
  }, [canView, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const validationError = validateReceiptPrefixTemplate(template);
  const dirty = template.trim() !== saved;

  const handleSave = async () => {
    if (!canEdit || validationError) return;
    setSaving(true);
    try {
      const response = await callApi({
        location: "madrasaSettings",
        method: "PATCH",
        data: { receiptPrefixTemplate: template.trim() },
      });
      const value = (response.data as SettingsPayload).receiptPrefixTemplate ?? template.trim();
      setSaved(value);
      setTemplate(value);
      notify("Madrasa settings saved", { type: "success" });
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Failed to save madrasa settings"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) return null;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      <Title title="Madrasa settings" />
      <Box sx={{ p: 2, maxWidth: 640 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Madrasa settings
        </Typography>

        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You can view these settings but not change them.
          </Alert>
        )}

        <TextField
          label="Fee receipt prefix"
          value={template}
          onChange={(event) => setTemplate(event.target.value)}
          disabled={!canEdit}
          fullWidth
          error={Boolean(validationError)}
          helperText={
            validationError ??
            "Use {yy-yy} for the academic year. Receipt numbers restart for each academic year."
          }
        />
        {!validationError && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Example for 2026-27: <strong>{previewReceiptPrefix(template.trim(), 2026)}</strong>
            <Box component="span" sx={{ ml: 0.5 }}>
              followed by the receipt number.
            </Box>
          </Typography>
        )}

        {canEdit && (
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            onClick={handleSave}
            disabled={saving || !dirty || Boolean(validationError)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </Box>
    </>
  );
}
